/**
 * AvailabilityProfile shared descriptor.
 *
 * Describes how one uploaded availability XLSX file maps to availability
 * facts, produced identically by every resolution tier (saved profile,
 * deterministic resolvers, generic fallback, and later a tier-3 AI resolver).
 * All column references are header strings (matching the file's detected
 * header row), not indices, so the profile stays stable if the API re-reads
 * the same file.
 *
 * Traceability: availability-profile-resolution / Requirement: AvailabilityProfile
 * shared descriptor.
 */
import { z } from 'zod';

export const AvailabilityTimeEncodingSchema = z.enum([
  'global',
  'month',
  'half_month',
  'date_range',
  'period_label_map',
]);
export type AvailabilityTimeEncoding = z.infer<typeof AvailabilityTimeEncodingSchema>;

export const AvailabilityPolaritySchema = z.enum(['list_of_free', 'list_of_busy']);
export type AvailabilityPolarity = z.infer<typeof AvailabilityPolaritySchema>;

/** Shared YYYY-MM-DD schema — reused by `availability-import.ts` (its `AvailabilityImportPreviewRowSchema.period`) instead of a second identical regex. */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

const AvailabilityProfileBaseSchema = z.object({
  headerRowIndex: z.number().int().nonnegative(),
  idColumns: z.object({
    grwIdColumn: z.string().nullable(),
    supplierNumberColumn: z.string().nullable(),
  }),
  supplierColumn: z.string().nullable(),
  /**
   * Operator-selected supplier the whole file belongs to. Used as the
   * fallback `supplierName` for every row when `supplierColumn` is `null`
   * (the common single-supplier-file case with no per-row `Dostawca`
   * column). Every resolution tier sets this to `null` — none can
   * auto-detect which platform supplier a file came from; the operator
   * fills it in during preview (same enforced-manual-entry pattern as
   * `globalPeriod`/`periodLabelMap`). Completeness relative to
   * `supplierColumn` is enforced at confirm time, not here — see
   * `availability-import-pipeline`.
   */
  defaultSupplierName: z.string().nullable(),
  gpsColumns: z.object({
    latColumn: z.string().nullable(),
    lngColumn: z.string().nullable(),
  }),
  addressColumns: z.object({
    addressColumn: z.string().nullable(),
    cityColumn: z.string().nullable(),
  }),
  timeEncoding: AvailabilityTimeEncodingSchema,
  /**
   * `periodStart`/`periodEnd` allow `''` in addition to `isoDateSchema` —
   * every resolution tier always produces a valid ISO date here, but the
   * web preview's editable period-columns table lets an operator clear a
   * row's date back to `''` before confirm. If this were `isoDateSchema`
   * alone, the DTO-layer `ZodValidationPipe` (registered as `APP_PIPE`,
   * runs before any controller/service code) would reject the whole
   * confirm request with a generic, untranslated `VALIDATION_ERROR` before
   * `assertConfirmProfileValid`'s dedicated `MONTH_PERIOD_COLUMNS_INCOMPLETE`
   * check (apps/api/src/availability-import/confirm-validation.ts) ever ran
   * — making that check unreachable in production. Allowing `''` here lets
   * the request through to the service layer, where the dedicated check
   * produces the friendly, translated error instead.
   */
  periodColumns: z.array(
    z.object({
      column: z.string(),
      periodStart: z.union([isoDateSchema, z.literal('')]),
      periodEnd: z.union([isoDateSchema, z.literal('')]),
    }),
  ),
  globalPeriod: z.object({ start: isoDateSchema, end: isoDateSchema }).nullable(),
  statusColumn: z.string().nullable(),
  dateRangeColumns: z.object({ startColumn: z.string(), endColumn: z.string() }).nullable(),
  /**
   * Column holding free-text period phrases (e.g. "cały czerwiec"), set only
   * when `timeEncoding = 'period_label_map'`.
   */
  periodLabelColumn: z.string().nullable(),
  /**
   * Operator-filled dictionary mapping each distinct raw phrase found in
   * `periodLabelColumn` to an explicit date range. Starts empty (`{}`) after
   * resolution — no automatic phrase-parsing is attempted. Set only when
   * `timeEncoding = 'period_label_map'`.
   */
  periodLabelMap: z
    .record(z.string(), z.object({ start: isoDateSchema, end: isoDateSchema }))
    .nullable(),
  /**
   * Per-column date-format override for any date-bearing `date_range`/
   * `month`/`half_month` column, keyed by that column's header. Optional —
   * `null`/omitted keys fall back to auto-detection.
   */
  dateFormatOverrides: z.record(z.string(), z.string()).nullable(),
  statusDictionary: z.record(z.string(), z.enum(['FREE', 'UNAVAILABLE'])),
  polarity: AvailabilityPolaritySchema,
});

/**
 * A `date_range` encoding without the two date columns cannot be executed —
 * the pipeline has no way to derive a period per row. A `period_label_map`
 * encoding without the column that carries the phrases is equally
 * inexecutable.
 */
export const AvailabilityProfileSchema = AvailabilityProfileBaseSchema.refine(
  (profile) => profile.timeEncoding !== 'date_range' || profile.dateRangeColumns !== null,
  {
    message: 'dateRangeColumns is required when timeEncoding is "date_range"',
    path: ['dateRangeColumns'],
  },
).refine(
  (profile) => profile.timeEncoding !== 'period_label_map' || profile.periodLabelColumn !== null,
  {
    message: 'periodLabelColumn is required when timeEncoding is "period_label_map"',
    path: ['periodLabelColumn'],
  },
);

export type AvailabilityProfile = z.infer<typeof AvailabilityProfileSchema>;
